/*
  AB#21909 — "Was this read right?": the desk's verdict on an ELSA reading
  itself. Right is one tap; wrong needs a reason and a line of text.
*/
import { http } from '@/interceptors/axiosRequestInterceptor';

export type ReadingVerdict = 'right' | 'wrong';
export type ReadingReason =
  | 'wrong_status'
  | 'missed_something'
  | 'out_of_date'
  | 'wrong_action'
  | 'other'
  // AB#21998 — a finance card: what the controller knows that the ledger does not.
  | 'already_paid'
  | 'not_due'
  | 'not_invoiceable'
  | 'wrong_amount';
/** 'finance_queue' is a verdict on a finance card, not on an ELSA reading. */
export type ReadingSource = 'needs_attention' | 'order_page' | 'finance_queue';

export interface MyReadingFeedback {
  verdict: ReadingVerdict;
  reason: ReadingReason | null;
  note: string | null;
}

export const READING_REASONS: { value: ReadingReason; label: string }[] = [
  { value: 'wrong_status', label: 'Wrong status' },
  { value: 'missed_something', label: 'Missed something' },
  { value: 'out_of_date', label: 'Out of date' },
  { value: 'wrong_action', label: 'Wrong next step' },
  { value: 'other', label: 'Other' },
];

/** AB#21998 — the reasons a finance card can be wrong. */
export const FINANCE_REASONS: { value: ReadingReason; label: string }[] = [
  { value: 'already_paid', label: 'Already paid' },
  { value: 'not_due', label: 'Not due yet' },
  { value: 'not_invoiceable', label: 'Should not be invoiced' },
  { value: 'wrong_amount', label: 'Wrong amount or currency' },
  { value: 'other', label: 'Other' },
];

export const reasonsFor = (source: ReadingSource): { value: ReadingReason; label: string }[] =>
  source === 'finance_queue' ? FINANCE_REASONS : READING_REASONS;

export const reasonLabel = (r: string | null | undefined): string =>
  [...READING_REASONS, ...FINANCE_REASONS].find((x) => x.value === r)?.label ?? r ?? '';

export const MIN_NOTE = 5;

export const sendReadingVerdict = async (
  orderItemId: number,
  verdict: ReadingVerdict,
  source: ReadingSource,
  reason: ReadingReason | null = null,
  note: string | null = null
): Promise<{ ok: boolean; message: string; feedback: MyReadingFeedback | null }> => {
  try {
    const body = {
      verdict,
      reason: verdict === 'wrong' ? reason : null,
      note: verdict === 'wrong' ? note : null,
    };
    // AB#21998 — a finance card is rated at the finance queue's own endpoint,
    // which reads the card on the server and needs only the order item.
    const { data } =
      source === 'finance_queue'
        ? await http.post(`needs-attention/finance/${orderItemId}/verdict`, body)
        : await http.post('elsa/readings/feedback', {
            order_item_id: orderItemId,
            ...body,
            source,
          });
    return {
      ok: data?.success !== false,
      message: String(data?.message ?? ''),
      feedback: (data?.result?.feedback as MyReadingFeedback) ?? null,
    };
  } catch (e: unknown) {
    const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return { ok: false, message: msg || 'The verdict could not be saved.', feedback: null };
  }
};
